// Step 5d Phase 3a — conflict resolution modal.
// Shows server_snapshot vs offline_version (+ field_diff for divergences) and presents
// ONLY the resolution actions the recon vocabulary supports, gated by class + role.
// Online-only: when offline, actions are disabled with the standard affordance.

import { useEffect, useState } from 'react';
import { C, F, M } from './theme';
import { ModalOverlay } from './atoms';
import { fetchConflict, resolveConflict, RESOLUTION_CODES, type ConflictItem } from '../../lib/conflicts';

interface Props {
  conflictId: string;
  canWorkflow: boolean; // ops/admin — required for slot_clash; either-party handled server-side
  online: boolean;
  onClose: () => void;
  onResolved: () => void; // refetch board + notifications
}

const fmt = (v: unknown): string => {
  if (v == null) return '—';
  if (typeof v === 'string') return v;
  return JSON.stringify(v);
};

export default function ConflictResolver({ conflictId, canWorkflow, online, onClose, onResolved }: Props) {
  const [conflict, setConflict] = useState<ConflictItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState('');

  useEffect(() => {
    let live = true;
    fetchConflict(conflictId).then((c) => { if (live) { setConflict(c); setLoading(false); } });
    return () => { live = false; };
  }, [conflictId]);

  async function choose(code: string) {
    setError(null);
    setBusy(true);
    const res = await resolveConflict(conflictId, code, note);
    setBusy(false);
    if (!res.ok) { setError(res.error ?? 'Resolution failed.'); return; }
    onResolved();
    onClose();
  }

  const label = (s: string) => <span style={{ fontSize: 9, fontWeight: 700, color: C.textMuted, letterSpacing: '0.06em' }}>{s}</span>;

  return (
    <ModalOverlay onClose={onClose}>
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 22, width: '100%', maxWidth: 460, maxHeight: '90vh', overflowY: 'auto', fontFamily: F }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>Resolve conflict</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.textMuted, cursor: 'pointer', fontSize: 18 }}>✕</button>
        </div>

        {loading && <div style={{ fontSize: 12, color: C.textMuted }}>Loading…</div>}
        {!loading && !conflict && <div style={{ fontSize: 12, color: C.red }}>Conflict not found.</div>}
        {!loading && conflict && conflict.status === 'resolved' && (
          <div style={{ background: C.greenLight, border: `1px solid ${C.green}55`, borderRadius: 6, padding: '8px 10px', fontSize: 11, color: C.green }}>
            ✓ Already resolved{conflict.resolution_code ? ` — ${conflict.resolution_code.replace(/_/g, ' ')}` : ''}.
          </div>
        )}

        {!loading && conflict && conflict.status === 'open' && (
          <>
            <div style={{ fontSize: 11, color: C.textDim, marginBottom: 10, lineHeight: 1.5 }}>
              {conflict.type === 'edit_divergence'
                ? 'This booking was edited offline and elsewhere before sync. Review both versions, then re-apply the offline edit or keep the server version.'
                : 'This offline booking clashed with an existing booking at sync. It does not hold the slot while conflicted.'}
            </div>

            {/* Field diff (edit_divergence) */}
            {conflict.type === 'edit_divergence' && conflict.field_diff && Object.keys(conflict.field_diff).length > 0 && (
              <div style={{ border: `1px solid ${C.border}`, borderRadius: 6, padding: 10, marginBottom: 12 }}>
                {label('CHANGED FIELDS (server → offline)')}
                <div style={{ marginTop: 6, display: 'grid', gap: 4 }}>
                  {Object.entries(conflict.field_diff).map(([k, d]) => (
                    <div key={k} style={{ fontSize: 11, fontFamily: M, color: C.textDim }}>
                      <span style={{ color: C.textMuted }}>{k}:</span> {fmt(d.from)} <span style={{ color: C.accent }}>→</span> {fmt(d.to)}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {error && <div style={{ background: C.redLight, border: `1px solid ${C.redBorder}`, borderRadius: 6, padding: '8px 10px', fontSize: 11, color: C.red, marginBottom: 10 }}>⚠ {error}</div>}

            <div style={{ marginBottom: 10 }}>
              {label('NOTE (optional)')}
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason / context"
                style={{ width: '100%', marginTop: 4, padding: '7px 9px', fontSize: 12, border: `1px solid ${C.border}`, borderRadius: 6, fontFamily: F, boxSizing: 'border-box' }} />
            </div>

            {!online && <div style={{ fontSize: 10, color: C.amber, marginBottom: 8 }}>⚠ Offline — resolution is unavailable until you reconnect.</div>}
            {conflict.type === 'slot_clash' && !canWorkflow && online && (
              <div style={{ fontSize: 10, color: C.amber, marginBottom: 8 }}>Only an ops controller or admin can resolve a slot conflict.</div>
            )}

            <div style={{ display: 'grid', gap: 8 }}>
              {RESOLUTION_CODES[conflict.type].map((opt) => {
                const disabled = busy || !online || (conflict.type === 'slot_clash' && !canWorkflow);
                return (
                  <button key={opt.code} onClick={() => choose(opt.code)} disabled={disabled}
                    style={{ textAlign: 'left', background: disabled ? C.pill : C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 8, padding: '9px 11px', cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.6 : 1, fontFamily: F }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{opt.label}</div>
                    <div style={{ fontSize: 10, color: C.textMuted, marginTop: 2, lineHeight: 1.4 }}>{opt.desc}</div>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
    </ModalOverlay>
  );
}
